/* General product add-ons. Keep these fields separate from legacy embroidery matching. */
(() => {
  function controlsFor(form) {
    return Array.from(document.querySelectorAll('product-add-ons')).find(controls => controls.form === form);
  }
  function selectedAddOns(form) {
    return Array.from(controlsFor(form)?.querySelectorAll('[data-add-on-input]:checked:not(:disabled)') || []);
  }
  function choicesFor(controls) {
    return Array.from(controls.querySelectorAll('[data-add-on-input]'), input => ({
      id: input.id, value: input.value, checked: input.checked,
      picker: input.closest('[data-add-on]').querySelector('add-on-variant-picker')?.snapshot()
    }));
  }
  function restoreChoices(controls, choices) {
    for (const choice of choices) {
      const input = Array.from(controls.querySelectorAll('[data-add-on-input]')).find(input => input.id === choice.id);
      if (!input) continue;
      const picker = input.closest('[data-add-on]').querySelector('add-on-variant-picker');
      if (picker && choice.picker) picker.restore(choice.picker);
      input.checked = !input.disabled && choice.checked;
    }
  }
  function ready(form) {
    const picker = Array.from(controlsFor(form)?.querySelectorAll('add-on-variant-picker') || [])
      .find(picker => picker.input?.checked && (picker.loading || picker.failed));
    if (!picker) return true;
    picker.status.textContent = picker.loading ? 'Please wait for these options to load.' : 'Please retry loading these options or remove this add-on.';
    picker.status.scrollIntoView({ block: 'nearest' });
    return false;
  }

  function appendItems(form, items) {
    if (Array.from(controlsFor(form)?.querySelectorAll('add-on-variant-picker') || []).some(picker => picker.input?.checked && (picker.loading || picker.failed))) {
      throw new Error('Please wait for the add-on options to finish loading, or retry.');
    }
    const selected = selectedAddOns(form);
    if (!selected.length) return items;
    const parent = items[0];
    const group = crypto.randomUUID();
    parent.properties = { ...parent.properties, _add_on_group: group };
    const title = controlsFor(form).dataset.productTitle;
    return items.concat(selected.map(input => ({
      id: Number(input.value),
      quantity: Number(parent.quantity || 1),
      parent_id: Number(parent.id),
      properties: { 'Add-on for': title, _add_on_group: group, _is_product_add_on: 'true' }
    })));
  }

  window.productAddOns = {
    appendItems, ready,
    saveSummary(form, properties) {
      properties.querySelector('[data-add-on-summary]')?.remove();
      const selected = selectedAddOns(form);
      if (!selected.length) return;
      const summary = document.createElement('div');
      summary.dataset.addOnSummary = '';
      selected.forEach(input => {
        const row = input.closest('[data-add-on]');
        const title = row.querySelector('[data-add-on-title]')?.textContent.trim() || '';
        const options = Array.from(row.querySelectorAll('[data-add-on-position]:checked'), option => option.dataset.optionValueName)
          .filter(Boolean).filter(value => value !== 'Default Title');
        const price = row.querySelector('[data-add-on-price]')?.textContent.trim();
        const item = document.createElement('p');
        item.className = 'embroidery-properties__item';
        item.textContent = `Add-on: ${title}${options.length ? ' — ' + options.join(' / ') : ''}${price ? ' (' + price + ')' : ''}`;
        summary.append(item);
      });
      properties.append(summary);
    },
    beginCustomization(form) {
      const controls = controlsFor(form);
      controls?.place();
      if (controls?.closest('[data-customization-add-ons]')) controls.draft = choicesFor(controls);
    },
    finishCustomization(form, saved) {
      const controls = controlsFor(form);
      if (!controls?.draft) return;
      if (!saved) restoreChoices(controls, controls.draft);
      delete controls.draft;
    },
    clearCustomization(form) {
      const controls = controlsFor(form);
      if (!controls?.closest('[data-customization-add-ons]')) return;
      controls.querySelectorAll('[data-add-on-input]').forEach(input => { input.checked = false; });
      delete controls.draft;
    }
  };

  // Fetch the add-on's own PDP so its assigned template, native swatches, images,
  // categories and option styles remain the source of truth. Never mount the PDP's
  // variant-picker controller: it would rerender or change the main product form.
  const pickerPages = new Map();
  async function pickerPage(url) {
    const key = url.toString();
    const cached = pickerPages.get(key);
    if (cached && Date.now() - cached.time < 30000) return cached.promise;
    const promise = fetch(key).then(response => {
      if (!response.ok) throw new Error('Options could not be loaded.');
      return response.text();
    }).catch(error => { pickerPages.delete(key); throw error; });
    if (pickerPages.size >= 50) pickerPages.delete(pickerPages.keys().next().value);
    pickerPages.set(key, { time: Date.now(), promise });
    return promise;
  }

  class AddOnVariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.controller && !this.controller.signal.aborted) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.row = this.closest('[data-add-on]');
      this.input = this.row.querySelector('[data-add-on-input]');
      this.content = this.querySelector('[data-picker-content]');
      this.status = this.querySelector('[data-picker-status]');
      this.retry = this.querySelector('[data-picker-retry]');
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-add-on-position]')) return;
        event.stopPropagation();
        const fields = Array.from(this.querySelectorAll('[data-add-on-position]:checked'))
          .sort((a, b) => Number(a.dataset.addOnPosition) - Number(b.dataset.addOnPosition));
        const missing = Array.from(this.querySelectorAll('[data-option-name][data-manual-selection-required]'))
          .find(group => !group.querySelector('input:checked'));
        if (missing) {
          this.input.disabled = true;
          this.status.textContent = `Please select ${missing.dataset.optionName}.`;
          return;
        }
        const url = new URL(event.target.dataset.productUrl || this.currentUrl || this.dataset.url, location.origin);
        url.searchParams.delete('variant');
        url.searchParams.set('option_values', fields.map(input => input.value).join(','));
        this.load(url, fields.map(input => input.dataset.optionValueName));
      }, options);
      this.addEventListener('click', event => {
        if (event.target.closest('[data-picker-retry]')) {
          this.load(this.lastUrl, this.expectedNames, this.initialLoad, this.chosenGroup);
          return;
        }
        const link = event.target.closest('[data-picker-content] a[href*="/products/"]');
        if (!link) return;
        event.preventDefault();
        this.load(new URL(link.href), null, true, link.closest('[data-option-name]')?.dataset.optionName);
      }, options);
      const form = this.closest('product-add-ons').form;
      const guard = event => {
        if (!this.input.checked || (!this.loading && !this.failed && !this.input.disabled)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.status.textContent = this.loading ? 'Please wait for these options to load.' : 'Choose available options or remove this add-on.';
        this.status.scrollIntoView({ block: 'nearest' });
      };
      form.addEventListener('submit', guard, { ...options, capture: true });
      form.addEventListener('click', event => {
        if (event.target.closest('.js-embroidery-atc-btn')) guard(event);
      }, { ...options, capture: true });
      const url = new URL(this.dataset.url, location.origin);
      url.searchParams.set('variant', this.dataset.variantId);
      this.load(url, null, true);
    }

    disconnectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) return;
        this.controller.abort();
        this.request = (this.request || 0) + 1;
      });
    }

    async load(url, expectedNames = null, initial = false, chosenGroup = null) {
      const request = this.request = (this.request || 0) + 1;
      this.initialLoad = initial;
      this.chosenGroup = chosenGroup;
      this.lastUrl = url;
      this.expectedNames = expectedNames;
      this.loading = true;
      this.failed = false;
      // Keep a previously selected checkbox removable while resolving its options.
      this.input.disabled = !this.input.checked;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Loading options…';
      this.retry.hidden = true;
      try {
        const html = await pickerPage(url);
        if (!this.isConnected || request !== this.request) return;
        const page = new DOMParser().parseFromString(html, 'text/html');
        const source = page.querySelector('variant-picker[context="main_product"]');
        if (!source) throw new Error('Options could not be loaded.');
        const variant = JSON.parse(source.querySelector('[data-variant]').textContent);
        const price = source.dataset.formattedPrice;
        const root = document.createElement('div');
        root.className = source.className;
        root.innerHTML = source.innerHTML;
        root.querySelectorAll('script, noscript, style').forEach(node => node.remove());
        root.querySelectorAll('.swatch-category').forEach(node => { node.hidden = !node.querySelector('input'); });
        // All IDs and form associations belong to this add-on instance, including popovers.
        const ids = new Map(Array.from(root.querySelectorAll('[id]'), node => [node.id, `${this.dataset.prefix}-${node.id}`]));
        root.querySelectorAll('*').forEach(node => {
          if (node.id) node.id = ids.get(node.id);
          for (const attr of ['for', 'aria-controls', 'aria-labelledby', 'aria-describedby']) {
            if (node.hasAttribute(attr)) node.setAttribute(attr, node.getAttribute(attr).split(' ').map(id => ids.get(id) || id).join(' '));
          }
          if (node.matches('input')) {
            node.setAttribute('form', `${this.dataset.prefix}-options`); // Deliberately outside the main form.
            node.name = `${this.dataset.prefix}-${node.name}`;
            if (node.dataset.optionPosition) {
              node.dataset.addOnPosition = node.dataset.optionPosition;
              delete node.dataset.optionPosition;
            }
          }
        });
        if (initial) {
          root.querySelectorAll('[data-manual-selection-required]').forEach(group => {
            if (group.dataset.optionName === chosenGroup) return;
            group.querySelectorAll('input:checked').forEach(input => { input.checked = false; });
            group.querySelectorAll('.is-selected').forEach(option => option.classList.remove('is-selected'));
            group.querySelectorAll('.variant-picker__option-info span').forEach(span => { span.textContent = ''; });
          });
        }
        const missing = Array.from(root.querySelectorAll('[data-manual-selection-required]')).find(group => !group.querySelector('input:checked'));
        const matches = !expectedNames || (variant?.options?.length === expectedNames.length && variant.options.every((name, i) => name === expectedNames[i]));
        const available = !!variant?.available && matches && !missing;
        // Keep an impossible combination visible instead of silently selecting a different SKU.
        if (matches || !expectedNames) {
          const focusedId = this.content.contains(document.activeElement) ? document.activeElement.id : null;
          this.content.replaceChildren(root);
          if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
        }
        const nextUrl = new URL(url);
        if (source.getAttribute('section-id')) nextUrl.searchParams.set('section_id', source.getAttribute('section-id'));
        this.currentUrl = nextUrl.toString();
        this.input.value = available ? variant.id : '';
        this.input.disabled = !available;
        if (!available) this.input.checked = false;
        const label = this.row.querySelector('[data-add-on-label]');
        const titleNode = this.row.querySelector('[data-add-on-title]') || this.row.querySelector('p');
        if (source.dataset.productTitle) titleNode.textContent = source.dataset.productTitle;
        const description = this.row.querySelector('[data-add-on-description]');
        if (description && source.hasAttribute('data-product-description')) description.textContent = source.dataset.productDescription;
        const title = titleNode.textContent;
        const action = !matches || !variant ? 'Unavailable' : !variant.available ? 'Sold out' : missing ? 'Choose options' : 'Add';
        label.replaceChildren(document.createTextNode(`${action} ${title}${price ? ' (' : ''}`));
        const priceNode = document.createElement('span');
        priceNode.dataset.addOnPrice = '';
        priceNode.textContent = price || '';
        if (price) label.append(priceNode, document.createTextNode(')'));
        this.status.textContent = missing ? `Please select ${missing.dataset.optionName}.` : available ? '' : 'This option combination is unavailable.';
      } catch (error) {
        if (request !== this.request || !this.isConnected) return;
        this.status.textContent = 'Options could not be loaded. Please retry.';
        this.retry.hidden = false;
        this.input.disabled = !this.input.checked;
        this.failed = true;
      } finally {
        if (request === this.request) {
          this.loading = false;
          this.removeAttribute('aria-busy');
        }
      }
    }

    snapshot() {
      this.content.querySelectorAll('input[type=radio]').forEach(input => input.toggleAttribute('checked', input.checked));
      return { title: this.row.querySelector('[data-add-on-title]')?.textContent, description: this.row.querySelector('[data-add-on-description]')?.textContent, url: this.currentUrl, value: this.input.value, checked: this.input.checked,
        html: this.content.innerHTML, status: this.status.textContent, label: this.row.querySelector('[data-add-on-label]').innerHTML,
        disabled: this.input.disabled, pending: this.loading || this.failed, lastUrl: this.lastUrl?.toString(), expectedNames: this.expectedNames, initial: this.initialLoad, chosenGroup: this.chosenGroup };
    }

    restore(state) {
      ++this.request;
      this.currentUrl = state.url;
      if (this.row.querySelector('[data-add-on-title]')) this.row.querySelector('[data-add-on-title]').textContent = state.title;
      if (this.row.querySelector('[data-add-on-description]')) this.row.querySelector('[data-add-on-description]').textContent = state.description;
      this.content.innerHTML = state.html;
      this.input.value = state.value;
      this.input.checked = state.checked;
      this.input.disabled = state.disabled;
      this.row.querySelector('[data-add-on-label]').innerHTML = state.label;
      this.status.textContent = state.status;
      this.loading = false;
      this.failed = false;
      this.retry.hidden = true;
      this.removeAttribute('aria-busy');
      if (state.pending) this.load(new URL(state.lastUrl), state.expectedNames, state.initial, state.chosenGroup);
    }
  }

  class ProductAddOns extends HTMLElement {
    get form() {
      return this.dataset.formId ? document.getElementById(this.dataset.formId) : this.closest('form');
    }

    place() {
      const form = this.form;
      const home = form?.querySelector('[data-add-on-home]');
      if (!home) return;
      const properties = Array.from(document.querySelectorAll('.js-embroidery-properties')).find(node => node.dataset.formId === form.getAttribute('id'));
      const slot = Array.from(document.querySelectorAll('[data-customization-add-ons]')).find(node => node.dataset.productId === this.dataset.productId);
      const excluded = this.dataset.customizationExcluded?.split(',').includes(form.querySelector('[name="id"]')?.value);
      const artwork = form.querySelector('fixed-frame-design');
      const canCustomize = (properties || artwork) && slot && !excluded && !form.classList.contains('embroidery-hidden');
      // Quick-buy sends customization customers to the PDP; it must not expose a second set of controls.
      const quickBuy = form.closest('quick-buy-modal');
      const customizationLink = quickBuy?.querySelector('a.embroidery-btn[href*="embroidery=true"]');
      this.hidden = !!customizationLink && !excluded && !form.classList.contains('embroidery-hidden');
      const destination = canCustomize ? slot : home;
      if (this.parentElement !== destination) destination.append(this);
      if (slot) slot.hidden = !slot.querySelector('product-add-ons');
    }

    connectedCallback() {
      if (this.controller && !this.controller.signal.aborted) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const form = this.form;
      const previous = Array.from(document.querySelectorAll('product-add-ons')).find(node => node !== this && this.dataset.formId && node.dataset.formId === this.dataset.formId && node.dataset.productId === this.dataset.productId);
      const previousChoices = previous && choicesFor(previous);
      const previousDraft = previous?.draft;
      previous?.remove();
      queueMicrotask(() => {
        if (!this.isConnected) return;
        if (previousChoices) restoreChoices(this, previousChoices);
        if (previousDraft) this.draft = previousDraft;
        this.place();
      });
      document.addEventListener('DOMContentLoaded', () => this.place(), options);
      form.addEventListener('change', event => {
        if (event.target.matches('[name="id"]')) queueMicrotask(() => this.place());
      }, options);
      form.addEventListener('product:rerender', event => {
        if (event.detail.productChange) return;
        const formId = form.getAttribute('id');
        const productId = this.dataset.productId;
        const choices = choicesFor(this);
        // Quick-buy replaces its entire form when the main variant resolves.
        // Restore only this product's choices after the theme completes that replacement.
        queueMicrotask(() => {
          const replacement = controlsFor(document.getElementById(formId));
          if (!replacement || replacement === this || !productId || replacement.dataset.productId !== productId) return;
          restoreChoices(replacement, choices);
        });
      }, { ...options, capture: true });
      form.addEventListener('variant:add', () => {
        this.querySelectorAll('[data-add-on-input]').forEach(input => { input.checked = false; });
        delete this.draft;
      }, options);
      form.addEventListener('formdata', event => {
        if (!selectedAddOns(form).length) return;
        const data = event.formData;
        const parentId = data.get('id');
        // Preserve files, selling plans and all existing line properties on the main item.
        for (const [key, value] of Array.from(data.entries())) {
          if (['id', 'quantity', 'selling_plan'].includes(key) || key.startsWith('properties[')) {
            const itemKey = key.startsWith('properties[') ? `items[0][properties]${key.slice(10)}` : `items[0][${key}]`;
            data.append(itemKey, value);
            data.delete(key);
          }
        }
        const items = appendItems(form, [{ id: parentId, quantity: data.get('items[0][quantity]') || 1 }]);
        data.set('items[0][properties][_add_on_group]', items[0].properties._add_on_group);
        items.slice(1).forEach((item, offset) => {
          const prefix = `items[${offset + 1}]`;
          data.set(`${prefix}[id]`, item.id);
          data.set(`${prefix}[quantity]`, item.quantity);
          data.set(`${prefix}[parent_id]`, item.parent_id);
          for (const [key, value] of Object.entries(item.properties)) data.set(`${prefix}[properties][${key}]`, value);
        });
      }, options);
    }

    disconnectedCallback() {
      queueMicrotask(() => { if (!this.isConnected) this.controller.abort(); });
    }
  }

  if (!customElements.get('product-add-ons')) customElements.define('product-add-ons', ProductAddOns);
  if (!customElements.get('add-on-variant-picker')) customElements.define('add-on-variant-picker', AddOnVariantPicker);


  let updatingGroup = false;
  async function changeGroup(event) {
    const control = event.type === 'click'
      ? event.target.closest('a[href*="/cart/change"]')
      : event.target.closest('input[data-line-key]');
    if (!control) return;
    const container = control.closest('line-item') || control.closest('.line-item-row');
    const groupData = container?.querySelector('[data-add-on-cart]');
    if (!groupData) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (updatingGroup) return;
    const targetQuantity = Number(event.type === 'click'
      ? new URL(control.href).searchParams.get('quantity') : control.value);
    const group = JSON.parse(groupData.textContent);
    const errorContainer = container.querySelector('.line-item-info') || container;
    errorContainer.querySelector('[data-add-on-error]')?.remove();
    const showError = message => {
      const error = document.createElement('p');
      error.dataset.addOnError = '';
      error.className = 'text-sm';
      error.setAttribute('role', 'alert');
      error.textContent = message;
      errorContainer.append(error);
    };
    if (!Number.isInteger(targetQuantity) || targetQuantity < 0) {
      showError('Please enter a valid quantity.');
      return;
    }
    const deltas = new Map();
    group.forEach(item => deltas.set(item.variantId, (deltas.get(item.variantId) || 0) + targetQuantity - item.quantity));
    const invalidQuantity = targetQuantity > 0 && group.some(item =>
      targetQuantity < item.min || (item.max != null && targetQuantity > item.max) || targetQuantity % item.step !== 0 ||
      (item.inventory != null && item.cartQuantity + deltas.get(item.variantId) > item.inventory)
    );
    if (invalidQuantity) {
      showError('That quantity is not available for this product and its add-ons.');
      container.querySelectorAll('quantity-selector').forEach(selector => selector.restoreDefaultValue?.());
      return;
    }
    updatingGroup = true;
    container.setAttribute('aria-busy', 'true');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    try {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: Object.fromEntries(group.map(item => [item.key, targetQuantity])),
          sections: sections.join(','),
          sections_url: window.location.pathname
        })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Unable to update these items. Please try again.');
      if (window.themeVariables.settings.pageType === 'cart') {
        window.location.reload();
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true, detail: { baseEvent: 'line-item:change', cart }
        }));
      }
    } catch (error) {
      showError(error.message || 'Unable to update these items. Please try again.');
      container.querySelectorAll('quantity-selector').forEach(selector => selector.restoreDefaultValue?.());
    } finally {
      updatingGroup = false;
      container.removeAttribute('aria-busy');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }
  document.addEventListener('click', changeGroup, true);
  document.addEventListener('change', changeGroup, true);
  document.addEventListener('submit', event => {
    if (!updatingGroup || !event.target.matches('form[action$="/cart"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
})();
